import axios from 'axios'
import * as Sentry from '@sentry/react'
import { API_BASE_URL, X_API_KEY } from '../services/api'

const handleAxiosError = (
  error: unknown,
  action: string,
  extras?: Record<string, unknown>,
) => {
  if (axios.isAxiosError(error)) {
    Sentry.captureException(error, {
      extra: {
        action,
        ...extras,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: action,
      },
    })
    throw error.response?.data || error
  } else {
    throw error
  }
}

const fetchEstablishments = async (): Promise<any[]> => {
  const FULL_API_URL = `${API_BASE_URL}/all/full`

  try {
    const response = await fetch(FULL_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch data')
    }
    return await response.json()
  } catch (error) {
    handleAxiosError(error, 'fetchEstablishments')
    return []
  }
}

const deleteMyEstablishment = async (
  announceId: number | string,
  token: string,
) => {
  const url = `${API_BASE_URL}/user/${announceId}/establishment`

  try {
    const response = await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    return response.data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'deleteMyEstablishment',
        announceId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'deleteMyEstablishment',
      },
    })
    throw error
  }
}

const fetchGalleryByUserId = async (userId: number): Promise<string> => {
  const GALLERY_API_URL = `${API_BASE_URL}/${userId}/gallery`

  try {
    const response = await fetch(GALLERY_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch gallery')
    }
    const galleryData = await response.json()
    return `path/to/your/images/${galleryData.fileName}`
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchGalleryByUserId',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchGalleryByUserId',
      },
    })
    throw error
  }
}

const fetchTimings = async (): Promise<string[]> => {
  const ANNOUNCES_API_URL = `${API_BASE_URL}/establishment`

  try {
    const response = await fetch(ANNOUNCES_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch announces')
    }
    const announcesData = await response.json()
    const timings = announcesData.flatMap((announce: any) => announce.timing)
    return Array.from(new Set(timings))
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchTimings',
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchTimings',
      },
    })
    throw error
  }
}

const postMyEstablishment = async (
  myEstablishment: any,
  token: string,
  userId: string,
): Promise<void> => {
  const POST_API_URL = `${API_BASE_URL}/user/${userId}/establishment`

  const requestBody = {
    userId: parseInt(userId),
    location: myEstablishment.location || [],
    practices: myEstablishment.practices || [],
    status: myEstablishment.status || [],
    email: myEstablishment.email || '',
    isAvailable: Boolean(myEstablishment.isAvailable),
    disponibility: myEstablishment.disponibility || [],
    availabilityStart: myEstablishment.availabilityStart || [],
    availabilityEnd: myEstablishment.availabilityEnd || [],
    smsOnly: Boolean(myEstablishment.smsOnly),
    callOnly: Boolean(myEstablishment.callOnly),
    whatsapp: Boolean(myEstablishment.whatsapp),
    emailOnly: Boolean(myEstablishment.emailOnly),
    price: myEstablishment.price || [],
    timing: myEstablishment.timing || [],
    priceEscort: myEstablishment.priceEscort || [],
    timingEscort: myEstablishment.timingEscort || [],
    showEscortPrices: Boolean(myEstablishment.showEscortPrices),
    title: myEstablishment.title || '',
    description: myEstablishment.description || '',
    boostedTime: myEstablishment.boostedTime || new Date().toISOString(),
  }

  try {
    const response = await axios.post(POST_API_URL, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Error details:', error.response?.data)
      throw error.response?.data || error
    }
    throw error
  }
}

const fetchEstablishmentByUserId = async (userId: string, token: string) => {
  const url = `${API_BASE_URL}/user/${userId}/establishment`

  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })
    return response.data.establishment
  } catch (error) {
    handleAxiosError(error, 'fetchEstablishmentByUserId', { userId })
  }
}

const updateLocalStorageWithUserEstablishment = (userEstablishments: any) => {
  if (
    !userEstablishments ||
    (Array.isArray(userEstablishments) && userEstablishments.length === 0)
  ) {
    localStorage.setItem('myEstablishment', JSON.stringify({}))
  } else {
    localStorage.setItem('myEstablishment', JSON.stringify(userEstablishments))
  }
}

const getUserEstablishment = async (
  userId: string,
  token: string,
): Promise<any> => {
  const USER_ANNOUNCE_API_URL = `${API_BASE_URL}/user/${userId}/establishment`

  try {
    const response = await fetch(USER_ANNOUNCE_API_URL, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch user announce')
    }
    const data = await response.json()
    return data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'getUserEstablishment',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'getUserEstablishment',
      },
    })
    throw error
  }
}

const updateMyEstablishment = async (
  dataToSend: any,
  token: string,
  userId: string,
) => {
  const url = `${API_BASE_URL}/user/${userId}/establishment`

  try {
    const response = await axios.patch(url, dataToSend, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': X_API_KEY,
      },
    })
    return response.data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'updateMyEstablishment',
        userId,
        dataToSend,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'updateMyEstablishment',
      },
    })
    if (axios.isAxiosError(error)) {
      throw error.response?.data || error
    } else {
      throw error
    }
  }
}

const fetchEstablishmentDetails = async (id: string): Promise<any> => {
  const ANNOUNCE_API_URL = `${API_BASE_URL}/establishments/${id}`

  try {
    const response = await fetch(ANNOUNCE_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch announce with id ${id}`)
    }
    const data = await response.json()
    return data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchEstablishmentDetails',
        id,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchEstablishmentDetails',
      },
    })
    throw error
  }
}

const fetchEstablishmentDetailsByUserId = async (userId: string) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/establishments?userId=${userId}`,
      {
        headers: {
          'x-api-key': X_API_KEY,
        },
      },
    )
    if (!response.ok) {
      throw new Error('Failed to fetch establishment details')
    }
    return response.json()
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchEstablishmentDetailsByUserId',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchEstablishmentDetailsByUserId',
      },
    })
    throw error
  }
}

export {
  fetchEstablishments,
  updateMyEstablishment,
  fetchEstablishmentByUserId,
  fetchEstablishmentDetailsByUserId,
  fetchGalleryByUserId,
  fetchEstablishmentDetails,
  fetchTimings,
  postMyEstablishment,
  deleteMyEstablishment,
  updateLocalStorageWithUserEstablishment,
  getUserEstablishment,
}
